import React, { useRef, useCallback, useEffect, useState } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { duration } from './primitives/tokens';
import { useControllable } from '../../hooks/useControllable';
import styles from './Slider.module.css';

interface SliderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'defaultValue'> {
  /** Controlled value (number or [min, max] for range) */
  value?: number | [number, number];
  /** Default value */
  defaultValue?: number | [number, number];
  /** Change handler */
  onChange?: (value: number | [number, number]) => void;
  /** Minimum */
  min?: number;
  /** Maximum */
  max?: number;
  /** Step */
  step?: number;
  /** Range mode (two handles) */
  range?: boolean;
  /** Disabled */
  disabled?: boolean;
  /** Vertical orientation */
  vertical?: boolean;
  /** Show tooltip on drag */
  tooltip?: boolean;
  /** Marks on the track */
  marks?: Record<number, React.ReactNode>;
}

// ── CVA definitions ──────────────────────────────────────────────────────────

const wrapper = cva(styles.wrapper, {
  variants: {
    vertical: {
      true: styles.wrapperVertical,
      false: styles.wrapperHorizontal,
    },
    disabled: {
      true: styles.wrapperDisabled,
    },
  },
  defaultVariants: {
    vertical: false,
  },
});

const track = cva(styles.track, {
  variants: {
    vertical: {
      true: styles.trackVertical,
      false: styles.trackHorizontal,
    },
    disabled: {
      true: styles.trackDisabled,
    },
  },
  defaultVariants: {
    vertical: false,
  },
});

const fill = cva(styles.fill, {
  variants: {
    vertical: {
      true: styles.fillVertical,
      false: styles.fillHorizontal,
    },
  },
  defaultVariants: {
    vertical: false,
  },
});

const thumb = cva(styles.thumb, {
  variants: {
    vertical: {
      true: styles.thumbVertical,
      false: styles.thumbHorizontal,
    },
    disabled: {
      true: styles.thumbDisabled,
    },
    dragging: {
      true: styles.thumbDragging,
    },
  },
  defaultVariants: {
    vertical: false,
  },
});

/**
 * Slider — range value selector with drag handle.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | value | number \| [number, number] | — | Controlled value |
 * | defaultValue | number \| [number, number] | 0 | Initial value |
 * | onChange | (value) => void | — | Change handler |
 * | min | number | 0 | Minimum |
 * | max | number | 100 | Maximum |
 * | step | number | 1 | Step increment |
 * | range | boolean | false | Two-handle range mode |
 * | disabled | boolean | false | Disable |
 * | vertical | boolean | false | Vertical orientation |
 * | tooltip | boolean | true | Show value tooltip on drag |
 * | marks | Record<number, ReactNode> | — | Label marks |
 *
 * @example
 * <Slider defaultValue={30} />
 * <Slider range defaultValue={[20, 80]} />
 * <Slider marks={{ 0: '0°', 50: '50°', 100: '100°' }} />
 */
export function Slider({
  value: controlledValue,
  defaultValue,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  range = false,
  disabled = false,
  vertical = false,
  tooltip: showTooltip = true,
  marks,
  className,
  style,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  ...props
}: SliderProps) {
  const defaultVal = defaultValue ?? (range ? ([min, max] as [number, number]) : min);
  const [value, setValue] = useControllable(controlledValue, defaultVal, onChange);
  const valueRef = useRef(value);
  valueRef.current = value;
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<null | 0 | 1>(null);

  const getPercent = (v: number) => ((v - min) / (max - min)) * 100;
  const fromPercent = useCallback(
    (p: number) => {
      const raw = min + (p / 100) * (max - min);
      const stepped = Math.round(raw / step) * step;
      return Math.min(max, Math.max(min, parseFloat(stepped.toFixed(10))));
    },
    [min, max, step],
  );

  const getPercentFromEvent = useCallback(
    (e: MouseEvent | React.MouseEvent) => {
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect) return 0;
      if (vertical) {
        return Math.min(100, Math.max(0, ((rect.bottom - e.clientY) / rect.height) * 100));
      }
      return Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100));
    },
    [vertical],
  );

  const handleMouseDown = useCallback(
    (thumbIndex: 0 | 1) => (e: React.MouseEvent) => {
      if (disabled) return;
      e.preventDefault();
      setDragging(thumbIndex);
    },
    [disabled],
  );

  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      const pct = getPercentFromEvent(e);
      const newVal = fromPercent(pct);
      const current = valueRef.current;
      if (range && Array.isArray(current)) {
        const distToLow = Math.abs(newVal - current[0]);
        const distToHigh = Math.abs(newVal - current[1]);
        if (distToLow < distToHigh) {
          setValue([newVal, current[1]]);
        } else {
          setValue([current[0], newVal]);
        }
      } else {
        setValue(newVal);
      }
    },
    [disabled, getPercentFromEvent, fromPercent, range, setValue],
  );

  useEffect(() => {
    if (dragging === null) return;
    const onMove = (e: MouseEvent) => {
      const pct = getPercentFromEvent(e);
      const newVal = fromPercent(pct);
      const current = valueRef.current;
      if (range && Array.isArray(current)) {
        const next: [number, number] = [...current] as [number, number];
        next[dragging] = newVal;
        if (next[0] > next[1]) next.reverse();
        setValue(next);
      } else {
        setValue(newVal);
      }
    };
    const onUp = () => setDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging, getPercentFromEvent, fromPercent, range, setValue]);

  const vals = range && Array.isArray(value) ? value : [value as number, value as number];
  const lowPct = getPercent(vals[0]);
  const highPct = range ? getPercent(vals[1]) : lowPct;

  const Thumb = ({ pct, index, val }: { pct: number; index: 0 | 1; val: number }) => (
    <motion.div
      role="slider"
      // The name belongs on the element carrying the role, not the wrapper:
      // a screen reader announcing the thumb would otherwise find it unnamed.
      // In range mode one name for both handles leaves the listener unable to
      // tell which end they are on, so each is qualified.
      aria-label={ariaLabel === undefined || !range
        ? ariaLabel
        : `${ariaLabel} ${index === 0 ? 'minimum' : 'maximum'}`}
      aria-labelledby={ariaLabelledBy}
      aria-valuenow={val}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={disabled ? -1 : 0}
      onMouseDown={handleMouseDown(index)}
      // initial={false} so the centring offset applies on the first paint
      // rather than being tweened from zero — without it the thumb visibly
      // slides onto the rail every time a slider mounts.
      initial={false}
      animate={{
        scale: dragging === index ? 1.2 : 1,
        x: '-50%',
        y: vertical ? '50%' : '-50%',
      }}
      transition={{ duration: duration.fast / 1000 }}
      className={thumb({
        vertical: vertical || undefined,
        disabled: disabled || undefined,
        dragging: dragging === index || undefined,
      })}
      style={
        vertical
          ? ({ bottom: `${pct}%` } as React.CSSProperties)
          : ({ left: `${pct}%` } as React.CSSProperties)
      }
    >
      {showTooltip && dragging === index && (
        <div className={styles.tooltip}>
          {val}
        </div>
      )}
    </motion.div>
  );

  // Compute fill CSS custom properties
  const fillStyle: React.CSSProperties = vertical
    ? {
        '--slider-fill-bottom': `${lowPct}%`,
        '--slider-fill-height': `${highPct - lowPct}%`,
      } as React.CSSProperties
    : {
        '--slider-fill-left': range ? `${lowPct}%` : '0',
        '--slider-fill-width': range ? `${highPct - lowPct}%` : `${lowPct}%`,
      } as React.CSSProperties;

  return (
    <div
      className={clsx(
        wrapper({ vertical: vertical || undefined, disabled: disabled || undefined }),
        className,
      )}
      style={style}
      {...props}
    >
      {/* Track */}
      <div
        ref={trackRef}
        onClick={handleTrackClick}
        className={track({ vertical: vertical || undefined, disabled: disabled || undefined })}
      >
        {/* Filled track */}
        <div
          className={fill({ vertical: vertical || undefined })}
          style={fillStyle}
        />

        {/* Thumbs */}
        {range ? (
          <>
            <Thumb pct={lowPct} index={0} val={vals[0]} />
            <Thumb pct={highPct} index={1} val={vals[1]} />
          </>
        ) : (
          <Thumb pct={lowPct} index={0} val={vals[0]} />
        )}
      </div>

      {/* Marks */}
      {marks && (
        <div className={styles.marks}>
          {Object.entries(marks).map(([k, label]) => {
            const pct = getPercent(Number(k));
            const markStyle: React.CSSProperties = vertical
              ? { bottom: `${pct}%`, left: 24 }
              : { left: `${pct}%`, top: 20 };
            return (
              <div
                key={k}
                className={styles.mark}
                style={markStyle}
              >
                {label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
